import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import { getArticle, ARTICLES } from "@/lib/blog";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = getArticle(slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar onNavigate={() => {}} />
        <div className="flex flex-col items-center justify-center min-h-screen text-center px-5">
          <h1 className="font-serif text-4xl text-white">Article not found</h1>
          <Link to="/blog" className="mt-6 text-gold text-sm uppercase tracking-[0.25em]">← Back to journal</Link>
        </div>
      </div>
    );
  }

  const more = ARTICLES.filter((a) => a.slug !== slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-black">
      <Navbar onNavigate={() => {}} />
      <article className="mx-auto max-w-3xl px-5 md:px-10 pt-36 pb-24">
        <button onClick={() => navigate("/blog")} className="mb-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 hover:text-gold transition-colors" data-testid="blog-back">
          <ArrowLeft size={14} /> Journal
        </button>
        <p className="text-[11px] uppercase tracking-[0.2em] text-gold">{article.date} · {article.readingTime}</p>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mt-4 font-serif font-light text-4xl md:text-6xl text-white leading-[0.95] tracking-tight">
          {article.title}
        </motion.h1>
        <div className="mt-10 aspect-[16/9] overflow-hidden border border-white/10">
          <img src={article.cover} alt={article.title} className="h-full w-full object-cover" />
        </div>
        <div className="mt-12 space-y-6">
          {article.body.map((block, i) =>
            block.type === "h" ? (
              <h2 key={i} className="font-serif text-2xl md:text-3xl text-gold pt-4">{block.text}</h2>
            ) : (
              <p key={i} className="text-lg font-light text-white/70 leading-relaxed">{block.text}</p>
            )
          )}
        </div>

        <div className="mt-14 border-t border-white/10 pt-10">
          <Link to="/" className="inline-flex items-center gap-2 bg-gold text-black px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-gold-light transition-colors" data-testid="blog-cta-book">
            Book a detail <ArrowUpRight size={14} />
          </Link>
        </div>
      </article>

      {more.length > 0 && (
        <section className="border-t border-white/10 py-20">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10">
            <p className="mb-8 text-xs uppercase tracking-mega text-gold">Keep reading</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {more.map((a) => (
                <Link key={a.slug} to={`/blog/${a.slug}`} className="group flex gap-5 items-center" data-testid={`blog-more-${a.slug}`}>
                  <div className="h-24 w-32 shrink-0 overflow-hidden border border-white/10">
                    <img src={a.cover} alt={a.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-white group-hover:text-gold transition-colors leading-tight">{a.title}</h3>
                    <p className="mt-1 text-xs text-white/40">{a.readingTime}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
